import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import PageHeader from "../../components/Elements/PageHeader/PageHeader";
import DataTable from "../../components/Elements/DataTable/DataTable";
import MainInput from "../../components/Elements/MainInput/MainInput";
import UserTransactionsTable from "../../components/Affiliation/UserTransactionsTable/UserTransactionsTable";
import AdminEditAffiliateModal from "../../components/AdminPages/AdminEditAffiliateModal";
import AffiliationService from "../../services/AffiliationService";
import "./AdminAffiliation.css";

const AdminAffiliation = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    date_start_inclusive: "",
    date_end_exclusive: "",
  });
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [tableVersion, setTableVersion] = useState(0);

  const userColumns = [
    { key: "email_private", label: t("affiliation.email", "User Email"), width: "220px" },
    { key: "total_amount", label: t("affiliation.net", "Net"), width: "130px" },
    { key: "provision", label: t("affiliation.provision", "Commission"), width: "130px" },
  ];

  const userCustomRenderers = {
    total_amount: (item) => {
      const val = item.total_amount;
      const currency = item.currency || "USD";
      return val !== undefined && val !== null ? `${Number(val).toFixed(2)} ${currency}` : "-";
    },
    provision: (item) => {
      const val = item.provision;
      const currency = item.currency || "USD";
      return val !== undefined && val !== null ? `${Number(val).toFixed(2)} ${currency}` : "-";
    },
  };

  const affiliateColumns = [
    { key: "affiliate_user", label: t("adminAffiliation.affiliateEmail", "Affiliate Email"), width: "220px" },
    { key: "first_mrg", label: t("affiliation.firstMrg", "First Mrg"), width: "110px" },
    { key: "one_off_mrg", label: t("affiliation.oneOffMrg", "One-Off"), width: "110px" },
    { key: "stats.total.total_users_registered", label: t("adminAffiliation.usersReg", "Users Reg."), width: "110px" },
    { key: "stats.total.total_amount", label: t("affiliation.net", "Net"), width: "130px" },
    {
      key: "stats.total.total_provision",
      label: t("adminAffiliation.totalProvision", "Total Provision"),
      width: "130px",
    },
    { key: "stats.total.total_link_entries", label: t("adminAffiliation.linkEntries", "Link Entries"), width: "110px" },
  ];

  const affiliateCustomRenderers = {
    first_mrg: (item) => {
      const val = item.first_mrg;
      return val !== undefined && val !== null ? `${Math.round(Number(val) * 100)}%` : "0%";
    },
    one_off_mrg: (item) => {
      const val = item.one_off_mrg;
      return val !== undefined && val !== null ? `${Math.round(Number(val) * 100)}%` : "0%";
    },
    "stats.total.total_amount": (item) => {
      const val = item.stats?.total?.total_amount;
      return val !== undefined && val !== null ? `${Number(val).toFixed(2)} USD` : "-";
    },
    "stats.total.total_provision": (item) => {
      const val = item.stats?.total?.total_provision;
      return val !== undefined && val !== null ? `${Number(val).toFixed(2)} USD` : "-";
    },
  };

  const fetchDataFn = useCallback(
    async (params) => {
      return await AffiliationService.getAdminAffiliateStats({
        ...params,
        dateStart: filters.date_start_inclusive || undefined,
        dateEnd: filters.date_end_exclusive || undefined,
      });
    },
    [filters.date_start_inclusive, filters.date_end_exclusive],
  );

  const handleEdit = (affiliate) => {
    setSelectedAffiliate(affiliate);
    setIsEditModalOpen(true);
  };

  const handleUpdated = () => {
    setTableVersion((v) => v + 1);
  };

  const rowActions = (item) => [
    {
      label: t("affiliation.editMrg", "Edit Mrg"),
      onClick: () => handleEdit(item),
    },
  ];

  const renderAffiliateUsersRow = (affiliateItem) => {
    const users = affiliateItem.stats?.filtered?.users || [];

    if (users.length === 0) {
      return (
        <div className="admin-affiliate-empty">
          {t("adminAffiliation.noUsers", "No registered users for this affiliate.")}
        </div>
      );
    }

    const fetchUsersDataSync = async () => ({
      items: users,
      total: users.length,
    });

    return (
      <div className="admin-affiliate-users-nested">
        <DataTable
          tableId={`affiliate-users-${affiliateItem.affiliate_user}`}
          fetchDataFunction={fetchUsersDataSync}
          columns={userColumns}
          itemKey="email_private"
          customRenderers={userCustomRenderers}
          renderExpandedRow={(userItem) => <UserTransactionsTable transactions={userItem.transactions || []} />}
          expandColumnLabel={t("affiliation.transactions", "Transactions")}
          showSort={false}
          showFilters={false}
          showPageSize={false}
          showCheckboxes={false}
          emptyMessage={t("adminAffiliation.noUsersFound", "No users found")}
        />
      </div>
    );
  };

  return (
    <div className="admin-affiliation-page">
      <PageHeader
        title={t("adminAffiliation.title", "Affiliation")}
        description={t(
          "adminAffiliation.subtitle",
          "View and manage all affiliates, their referred users, and associated transactions.",
        )}
      />

      <div className="admin-affiliation-date-filters">
        <div className="admin-affiliation-date-filters__row">
          <div className="admin-affiliation-date-filters__field">
            <label className="dt-label">{t("affiliation.fromDate", "From Date")}</label>
            <MainInput
              type="date"
              value={filters.date_start_inclusive || ""}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  date_start_inclusive: e.target.value,
                }))
              }
            />
          </div>

          <div className="admin-affiliation-date-filters__field">
            <label className="dt-label">{t("affiliation.toDate", "To Date")}</label>
            <MainInput
              type="date"
              value={filters.date_end_exclusive || ""}
              onChange={(e) =>
                setFilters((prev) => ({
                  ...prev,
                  date_end_exclusive: e.target.value,
                }))
              }
            />
          </div>
        </div>
      </div>

      <DataTable
        key={`${tableVersion}-${filters.date_start_inclusive}-${filters.date_end_exclusive}`}
        tableId="admin-affiliation-main"
        fetchDataFunction={fetchDataFn}
        columns={affiliateColumns}
        itemKey="affiliate_user"
        customRenderers={affiliateCustomRenderers}
        rowActions={rowActions}
        rowActionsMode="buttons"
        actionsColumnLabel={t("table.actions", "Actions")}
        renderExpandedRow={renderAffiliateUsersRow}
        expandColumnLabel={t("adminAffiliation.users", "Users")}
        showSort={false}
        showFilters={false}
        showPageSize={false}
        defaultPageSize={5}
        showCheckboxes={false}
      />

      <AdminEditAffiliateModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        affiliate={selectedAffiliate}
        onUpdated={handleUpdated}
      />
    </div>
  );
};

export default AdminAffiliation;
